import React, { FC } from 'react'
import { graphql, useFragment } from 'react-relay'
import { useMatch } from 'react-router-dom'

import { Button } from '../Button'
import { Element } from '../Element'
import { Link } from '../Link'
import { LinkText, Text } from '../Text'
import { Stack } from '../Stack'
import { GraphQLImage } from '../GraphQLImage'
import { Icon } from '../Icon'
import { FormattedMarkdown } from '@newComponents'

import { GuildPrimaryPhoto } from '../Guild/GuildPrimaryPhoto'

import { UserPrimaryPhoto } from './UserPrimaryPhoto'
import { UserBackgroundPhoto } from './UserBackgroundPhoto'

import { useInEmbed } from '../Embed'

import {
  UserCard_user$data,
  UserCard_user$key,
} from './__generated__/UserCard_user.graphql'
import {
  UserCard_GuildMembershipBadgesFragment$data,
  UserCard_GuildMembershipBadgesFragment$key,
} from './__generated__/UserCard_GuildMembershipBadgesFragment.graphql'

const ALLOWED_ELEMENTS = ['a', 'p']

export const UserCard = ({
  user,
  expand,
  limitDescriptionLines,
}: {
  user: UserCard_user$key
  expand?: boolean
  limitDescriptionLines?: number
}) => {
  const data = useFragment(
    graphql`
      fragment UserCard_user on User {
        id
        rowId
        slugId
        firstName
        lastName
        bio

        isMe
        isFollowing

        backgroundPhoto {
          id
        }

        ...UserFollowingStatus_user
        ...UserPrimaryPhoto_user
        ...UserBackgroundPhoto_user

        organizerGuildMemberships: guildMemberships(
          first: 3
          condition: { membershipLevel: ORGANIZER }
        ) {
          ...UserCard_GuildMembershipBadgesFragment
        }
        editorGuildMemberships: guildMemberships(
          first: 3
          condition: { membershipLevel: EDITOR }
        ) {
          ...UserCard_GuildMembershipBadgesFragment
        }
      }
    `,
    user
  )

  const isUserScreen = useMatch(`/${data.slugId}/*`)

  return (
    <Stack expand={expand}>
      <Element component='ActorCard' tokens={expand ? { flex: 1 } : undefined}>
        <Stack gap={4} expand={expand}>
          <Stack>
            <Element component='ActorCardPhotos'>
              <UserBackgroundPhoto user={data} />

              <Element tokens={{ marginTop: -10 }}>
                <UserPrimaryPhoto size={20} user={data} variant='inCard' />
              </Element>
            </Element>
          </Stack>

          <Stack expand={expand}>
            <Element
              component='ActorCardInfo'
              tokens={expand ? { flex: 1 } : undefined}
            >
              <Stack gap={4} expand={expand}>
                <Stack gap={2}>
                  <Link to={`/${data.slugId}`}>
                    <Text numberOfLines={1} size={6} variant='heading'>
                      {data.firstName} {data.lastName}
                    </Text>
                  </Link>

                  {!!data.bio &&
                    (isUserScreen ? (
                      <FormattedMarkdown
                        allowedElements={ALLOWED_ELEMENTS}
                        formattedVariant='PLAIN_TEXT'
                        numberOfLines={limitDescriptionLines}
                        textSize='$4'
                      >
                        {data.bio}
                      </FormattedMarkdown>
                    ) : (
                      <Link to={`/${data.slugId}`}>
                        <FormattedMarkdown
                          allowedElements={ALLOWED_ELEMENTS}
                          formattedVariant='PLAIN_TEXT'
                          numberOfLines={limitDescriptionLines}
                          textSize='$4'
                        >
                          {data.bio}
                        </FormattedMarkdown>
                      </Link>
                    ))}
                </Stack>

                <BadgesSection user={data} />
              </Stack>

              <ViewProfileButton user={data} />
            </Element>
          </Stack>

          <CallToActionButton user={data} />
        </Stack>
      </Element>
    </Stack>
  )
}

const MEMBERSHIPS_FRAGMENT = graphql`
  fragment UserCard_GuildMembershipBadgesFragment on GuildMembershipsConnection {
    edges {
      node {
        id
        membershipLevel
        guild {
          slugId
          primaryPhoto {
            ...GraphQLImage_image
          }

          ...GuildPrimaryPhoto_guild
        }
      }
    }
  }
`

const BadgesSection: FC<{ user: UserCard_user$data }> = ({ user }) => {
  const organizerFragment =
    useFragment<UserCard_GuildMembershipBadgesFragment$key>(
      MEMBERSHIPS_FRAGMENT,
      user.organizerGuildMemberships
    )
  const editorFragment =
    useFragment<UserCard_GuildMembershipBadgesFragment$key>(
      MEMBERSHIPS_FRAGMENT,
      user.editorGuildMemberships
    )

  const badgedGuildsWithPrimaryPhoto = [
    ...organizerFragment.edges.filter(
      (edge) => edge.node?.guild?.slugId && edge.node?.guild?.primaryPhoto
    ),
    ...editorFragment.edges.filter(
      (edge) => edge.node?.guild?.slugId && edge.node?.guild?.primaryPhoto
    ),
  ].slice(0, 4)

  if (!badgedGuildsWithPrimaryPhoto.length) {
    return null
  }

  return (
    <Stack direction='horizontal' alignItems='flex-start' gap={3}>
      {badgedGuildsWithPrimaryPhoto.map((edge) => {
        // This is filtered above, but needed for TypeScript....
        if (!edge.node?.guild?.slugId || !edge.node?.guild?.primaryPhoto) {
          return null
        }

        return <BadgeLink key={edge.node.id} guildMembership={edge.node} />
      })}
    </Stack>
  )
}

const BadgeLink: FC<{
  guildMembership: NonNullable<
    NonNullable<UserCard_GuildMembershipBadgesFragment$data['edges'][0]['node']>
  >
}> = ({ guildMembership }) => {
  if (!guildMembership.guild?.primaryPhoto) {
    return null
  }

  return (
    <Link to={`/${guildMembership.guild.slugId}`}>
      <Element tokens={{ height: 10 }} style={{ gap: 0 }}>
        <GuildPrimaryPhoto guild={guildMembership.guild} size={10} />

        <BadgeLinkIcon membershipLevel={guildMembership.membershipLevel} />
      </Element>
    </Link>
  )
}

const BadgeLinkIcon: FC<{
  membershipLevel: NonNullable<
    NonNullable<
      NonNullable<UserCard_GuildMembershipBadgesFragment$data['edges'][0]['node']>
    >['membershipLevel']
  >
}> = ({ membershipLevel }) => {
  // Web-only style for badge icons (cast through unknown since these are web-only props)
  const badgeStyle = {
    borderRadius: '50%',
    background:
      'linear-gradient(0deg, #5533EB 28.13%, rgba(85, 51, 235, 0) 159.37%), #FFFFFF',
    boxShadow: 'inset 0px 0px 0px 3px rgba(27, 19, 64, 0.2)',
  } as unknown as import('react-native').ViewStyle

  switch (membershipLevel) {
    case 'ORGANIZER':
      return (
        <Element
          tokens={{
            bottom: 12.5,
            left: 7,
            width: 6,
            height: 6,
            alignItems: 'center',
            justifyContent: 'center',
            color: 'grey.0',
            borderColor: 'grey.0',
            borderWidth: 0.5,
          }}
          style={badgeStyle}
        >
          <Icon name='star-filled' variant='button' size={3.5} />
        </Element>
      )
    case 'EDITOR':
      return (
        <Element
          tokens={{
            bottom: 12.5,
            left: 7,
            width: 6,
            height: 6,
            alignItems: 'center',
            justifyContent: 'center',
            color: 'grey.0',
            borderColor: 'grey.0',
            borderWidth: 0.5,
          }}
          style={badgeStyle}
        >
          <Icon name='ribbon-filled' variant='button' size={3.5} />
        </Element>
      )
    default:
      return null
  }
}

const ViewProfileButton: FC<{ user: UserCard_user$data }> = ({ user }) => {
  const inEmbed = useInEmbed()

  if (!inEmbed) {
    return null
  }

  return (
    <Link to={`/${user.slugId}`}>
      <Button variant='secondary'>
        <Text variant='buttonSecondary' size={4}>
          View {user.firstName}'s Profile
        </Text>
      </Button>
    </Link>
  )
}

const CallToActionButton: FC<{ user: UserCard_user$data }> = ({ user }) => {
  // useMatch matches against location data which encodes any unicode slugs
  const match = useMatch(`/${encodeURIComponent(user.slugId)}/*`)

  if (!user.isMe) {
    return null
  }

  if (match !== null) {
    return (
      <Stack>
        <Link to={`/${user.slugId}/settings`}>
          <Button variant='callToAction' hoverVariant='callToActionHover'>
            <Stack
              direction='horizontal'
              gap={2}
              justifyContent='center'
              alignItems='center'
            >
              <Icon
                name='settings-filled'
                variant='callToActionButton'
                size={4}
              />

              <Text variant='callToActionButton' size={4}>
                Settings
              </Text>
            </Stack>
          </Button>
        </Link>
      </Stack>
    )
  }

  return (
    <Stack>
      <Link to={`/${user.slugId}`}>
        <Button variant='callToAction' hoverVariant='callToActionHover'>
          <Stack
            direction='horizontal'
            gap={2}
            justifyContent='center'
            alignItems='center'
          >
            <Icon name='person' variant='callToActionButton' size={4} />
            <Text variant='callToActionButton' size={4}>
              Go to my profile
            </Text>
          </Stack>
        </Button>
      </Link>
    </Stack>
  )
}
